/**
 * Modal that opens when a scrape_queue row is clicked on the run-detail
 * page. Four tabs cover every aspect of "what got captured for this URL":
 *
 *   Structured data → the parsed JSON output (scrape_queue.scraped_data)
 *   Metadata        → URL, adapter, suburb, status, attempts, winning
 *                     provider (joined from scrape_provider_attempts),
 *                     scraped_at, is_out_of_range, last_error
 *   Files           → future: images / PDFs etc. captured per-scrape.
 *                     Placeholder for now so the tab schema is fixed.
 *   Snapshot        → raw HTML/markdown the provider returned (lazy-loaded
 *                     from Supabase Storage via page_snapshots row)
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, ExternalLink, FileJson } from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { ScrollArea } from "@repo/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { downloadStoredPage, type JobRunDetailQueueRow } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

interface ScrapedDataDialogProps {
  row: JobRunDetailQueueRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ProviderAttemptRow {
  provider: string;
  status: string | null;
  strategy: string | null;
  attempt_order: number | null;
  http_status: number | null;
  duration_ms: number | null;
  completeness_score: number | null;
  created_at: string;
}

interface PageSnapshotRow {
  id: string;
  storage_bucket: string;
  storage_path: string;
  content_type: string | null;
  content_encoding: string | null;
  size_bytes: number | null;
  captured_at: string;
  provider: string | null;
}

function StatusPill({ row }: { row: JobRunDetailQueueRow }) {
  if (row.is_out_of_range) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
        out of range
      </Badge>
    );
  }
  if (row.status === "completed") {
    return (
      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">
        scraped
      </Badge>
    );
  }
  if (row.status === "failed") {
    return <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/30">failed</Badge>;
  }
  return <Badge variant="outline" className="text-muted-foreground">{row.status}</Badge>;
}

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-sm py-1.5 border-b last:border-b-0">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono break-all">{value}</div>
    </div>
  );
}

function StructuredDataTab({ data }: { data: Record<string, unknown> | null }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">
        No structured data captured.
      </div>
    );
  }
  return (
    <ScrollArea className="h-[60vh]">
      <pre className="text-xs font-mono whitespace-pre-wrap bg-muted/30 p-3 rounded">
        {JSON.stringify(data, null, 2)}
      </pre>
    </ScrollArea>
  );
}

function MetadataTab({
  row,
  provider,
  isLoadingProvider,
}: {
  row: JobRunDetailQueueRow;
  provider: ProviderAttemptRow | null;
  isLoadingProvider: boolean;
}) {
  return (
    <div className="space-y-1">
      <MetadataRow
        label="URL"
        value={
          <a
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {row.url}
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        }
      />
      <MetadataRow label="Adapter" value={`${row.adapter_name} (${row.adapter_slug})`} />
      <MetadataRow
        label="Suburb"
        value={`${row.suburb_name}, ${row.state} ${row.postcode}`}
      />
      <MetadataRow label="Status" value={<StatusPill row={row} />} />
      <MetadataRow label="Attempts" value={String(row.attempts)} />
      <MetadataRow label="Out of range" value={row.is_out_of_range ? "yes" : "no"} />
      <MetadataRow
        label="Scraped at"
        value={row.scraped_at ? formatDate(row.scraped_at, { includeTime: true }) : "—"}
      />
      <MetadataRow
        label="Provider"
        value={
          isLoadingProvider ? (
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              loading…
            </span>
          ) : provider ? (
            <span>
              {provider.provider}
              {provider.strategy ? (
                <span className="text-muted-foreground ml-2">({provider.strategy})</span>
              ) : null}
              {provider.http_status ? (
                <span className="text-muted-foreground ml-2">HTTP {provider.http_status}</span>
              ) : null}
              {provider.duration_ms ? (
                <span className="text-muted-foreground ml-2">{provider.duration_ms}ms</span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">no provider attempt recorded</span>
          )
        }
      />
      {row.last_error && (
        <MetadataRow
          label="Last error"
          value={<span className="text-red-600">{row.last_error}</span>}
        />
      )}
    </div>
  );
}

function FilesTab() {
  return (
    <div className="py-12 text-center text-muted-foreground text-sm">
      <FileJson className="w-10 h-10 mx-auto mb-3 opacity-50" />
      <p>No files captured for this scrape.</p>
      <p className="text-xs mt-1">Images, PDFs, and other artefacts will appear here once capture is enabled.</p>
    </div>
  );
}

function SnapshotTab({
  snapshot,
  isLoadingSnapshot,
  bodyText,
  isLoadingBody,
}: {
  snapshot: PageSnapshotRow | null;
  isLoadingSnapshot: boolean;
  bodyText: string | null;
  isLoadingBody: boolean;
}) {
  if (isLoadingSnapshot) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!snapshot) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">
        <p>No raw snapshot captured for this URL.</p>
        <p className="text-xs mt-1">
          Snapshots are written by the scrape worker on successful providers; absent rows usually
          mean the scrape predates snapshot capture or failed before capture ran.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{snapshot.content_type || "unknown content-type"}</span>
        {snapshot.size_bytes ? <span>{(snapshot.size_bytes / 1024).toFixed(1)}KB</span> : null}
        <span>captured {formatDate(snapshot.captured_at, { includeTime: true })}</span>
        {snapshot.provider ? <span>via {snapshot.provider}</span> : null}
      </div>
      {isLoadingBody ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ScrollArea className="h-[55vh]">
          <pre className="text-[11px] font-mono whitespace-pre-wrap bg-muted/30 p-3 rounded">
            {bodyText ?? "(empty body)"}
          </pre>
        </ScrollArea>
      )}
    </div>
  );
}

export function ScrapedDataDialog({ row, open, onOpenChange }: ScrapedDataDialogProps) {
  const [activeTab, setActiveTab] = useState<string>("structured");
  const [provider, setProvider] = useState<ProviderAttemptRow | null>(null);
  const [isLoadingProvider, setIsLoadingProvider] = useState(false);
  const [snapshot, setSnapshot] = useState<PageSnapshotRow | null>(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [bodyText, setBodyText] = useState<string | null>(null);
  const [isLoadingBody, setIsLoadingBody] = useState(false);

  const queueItemId = row?.id ?? null;

  // Reset state when the row changes (or the dialog reopens with a new row).
  // biome-ignore lint/correctness/useExhaustiveDependencies: queueItemId is the reset trigger
  useEffect(() => {
    setActiveTab("structured");
    setProvider(null);
    setSnapshot(null);
    setBodyText(null);
  }, [queueItemId]);

  // Provider attempt — fetch when the Metadata tab is first visited.
  useEffect(() => {
    if (!open || !queueItemId || activeTab !== "metadata" || provider !== null || isLoadingProvider) {
      return;
    }
    let cancelled = false;
    setIsLoadingProvider(true);
    (supabase as any)
      .from("scrape_provider_attempts")
      .select("provider, status, strategy, attempt_order, http_status, duration_ms, completeness_score, created_at")
      .eq("queue_item_id", queueItemId)
      .order("attempt_order", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: ProviderAttemptRow | null }) => {
        if (cancelled) return;
        setProvider(res.data ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error("ScrapedDataDialog: provider fetch failed", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProvider(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, queueItemId, activeTab, provider, isLoadingProvider]);

  // Snapshot row — fetch when Snapshot tab is first visited.
  useEffect(() => {
    if (!open || !queueItemId || activeTab !== "snapshot" || snapshot !== null || isLoadingSnapshot) {
      return;
    }
    let cancelled = false;
    setIsLoadingSnapshot(true);
    (supabase as any)
      .from("page_snapshots")
      .select(
        "id, storage_bucket, storage_path, content_type, content_encoding, size_bytes, captured_at, provider",
      )
      .eq("queue_item_id", queueItemId)
      .order("captured_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: PageSnapshotRow | null }) => {
        if (cancelled) return;
        setSnapshot(res.data ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error("ScrapedDataDialog: snapshot row fetch failed", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSnapshot(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, queueItemId, activeTab, snapshot, isLoadingSnapshot]);

  // Snapshot body — lazy-load once we know the storage path.
  useEffect(() => {
    if (!open || !snapshot || activeTab !== "snapshot" || bodyText !== null || isLoadingBody) {
      return;
    }
    let cancelled = false;
    setIsLoadingBody(true);
    downloadStoredPage(snapshot)
      .then((text) => {
        if (cancelled) return;
        setBodyText(text.length > 200_000 ? `${text.slice(0, 200_000)}\n…(truncated to 200KB)` : text);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error("ScrapedDataDialog: snapshot body fetch failed", err);
          setBodyText(`(failed to load snapshot body: ${err instanceof Error ? err.message : String(err)})`);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBody(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, snapshot, activeTab, bodyText, isLoadingBody]);

  const title = useMemo(() => {
    if (!row) return "";
    return `${row.suburb_name} · ${row.adapter_slug}`;
  }, [row]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            {row && <StatusPill row={row} />}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs break-all">
            {row?.url}
          </DialogDescription>
        </DialogHeader>
        {row && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="structured">Structured data</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
              <TabsTrigger value="snapshot">Snapshot</TabsTrigger>
            </TabsList>
            <TabsContent value="structured" className="mt-3">
              <StructuredDataTab data={row.scraped_data} />
            </TabsContent>
            <TabsContent value="metadata" className="mt-3">
              <MetadataTab
                row={row}
                provider={provider}
                isLoadingProvider={isLoadingProvider}
              />
            </TabsContent>
            <TabsContent value="files" className="mt-3">
              <FilesTab />
            </TabsContent>
            <TabsContent value="snapshot" className="mt-3">
              <SnapshotTab
                snapshot={snapshot}
                isLoadingSnapshot={isLoadingSnapshot}
                bodyText={bodyText}
                isLoadingBody={isLoadingBody}
              />
            </TabsContent>
          </Tabs>
        )}
        <div className="flex justify-end pt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
